import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  clearOptionalBrowserStorage,
  readBrowserStorageConsent,
  saveBrowserStorageConsent,
  type BrowserStorageConsent,
} from "../browser-storage";
import { disableAnalytics, initializeAnalytics, resetAnalytics } from "../analytics";

type BrowserStorageConsentContextValue = {
  consent: BrowserStorageConsent;
  isOptionalStorageEnabled: boolean;
  isBannerOpen: boolean;
  acceptOptionalStorage: () => void;
  rejectOptionalStorage: () => void;
  openPreferences: () => void;
};

const BrowserStorageConsentContext =
  createContext<BrowserStorageConsentContextValue | null>(null);

export function BrowserStorageConsentProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [consent, setConsent] = useState<BrowserStorageConsent>(() =>
    readBrowserStorageConsent()
  );
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    if (consent === "undecided") clearOptionalBrowserStorage();
  }, [consent]);

  useEffect(() => {
    if (consent === "accepted") {
      void initializeAnalytics();
    } else {
      disableAnalytics();
      resetAnalytics();
    }
  }, [consent]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (new URLSearchParams(window.location.search).get("storagePreferences") === "open") {
      setPreferencesOpen(true);
    }
  }, []);

  const acceptOptionalStorage = useCallback(() => {
    saveBrowserStorageConsent("accepted");
    setConsent("accepted");
    setPreferencesOpen(false);
  }, []);

  const rejectOptionalStorage = useCallback(() => {
    saveBrowserStorageConsent("rejected");
    setConsent("rejected");
    setPreferencesOpen(false);
  }, []);

  const openPreferences = useCallback(() => setPreferencesOpen(true), []);

  const value = useMemo(
    () => ({
      consent,
      isOptionalStorageEnabled: consent === "accepted",
      isBannerOpen: consent === "undecided" || preferencesOpen,
      acceptOptionalStorage,
      rejectOptionalStorage,
      openPreferences,
    }),
    [
      acceptOptionalStorage,
      consent,
      openPreferences,
      preferencesOpen,
      rejectOptionalStorage,
    ]
  );

  return (
    <BrowserStorageConsentContext.Provider value={value}>
      {children}
    </BrowserStorageConsentContext.Provider>
  );
}

export function useBrowserStorageConsent() {
  const context = useContext(BrowserStorageConsentContext);
  if (!context)
    throw new Error(
      "useBrowserStorageConsent must be used inside BrowserStorageConsentProvider"
    );
  return context;
}
